import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ExportMenuItem } from "@design-system/ExportMenuItem/ExportMenuItem";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { HStack } from "@design-system/HStack/HStack";
import { ChartOfAccountsTemplateRow } from "../chartOfAccountsTemplatesViewConfig";
import { ChartOfAccountsTemplateTableMeta } from "../ChartOfAccountsTemplatesList";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    COA_TEMPLATE_ARCHIVE_FORBIDDEN_TOOLTIP,
    COA_TEMPLATE_DELETE_FORBIDDEN_TOOLTIP,
    COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP,
} from "../constants";
import { useChartOfAccountsTemplateCsvDownload } from "../useChartOfAccountsTemplateCsvDownload";

export const ChartOfAccountsTemplateActivityAction = ({
    canManage,
    onSelect,
}: {
    canManage: boolean;
    onSelect: () => void;
}) => (
    <Menu.Item disabled={!canManage} onClick={onSelect}>
        Activity
    </Menu.Item>
);

export const ChartOfAccountsTemplateArchiveAction = ({
    isArchived,
    canArchive,
    disabledReason,
    onSelect,
}: {
    isArchived: boolean;
    canArchive: boolean;
    disabledReason: string;
    onSelect: () => void;
}) => (
    <Menu.Item
        disabled={!canArchive}
        disabledReason={disabledReason}
        onClick={onSelect}
    >
        {isArchived ? "Enable" : "Archive"}
    </Menu.Item>
);

export const ChartOfAccountsTemplateActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ChartOfAccountsTemplateRow, string>) => {
        const meta = (table.options.meta ??
            {}) as ChartOfAccountsTemplateTableMeta;
        const templateId = row.original.id;
        const { download: downloadCsv, isDownloading } =
            useChartOfAccountsTemplateCsvDownload();

        if (
            !meta.callbacks?.onApplyTemplate ||
            !meta.callbacks?.onEditTemplate ||
            !meta.callbacks?.onEditTemplateAccounts ||
            !meta.callbacks?.onDuplicateTemplate ||
            !meta.callbacks?.onDeleteTemplate ||
            !meta.callbacks?.onArchiveToggleTemplate ||
            !meta.callbacks?.onViewActivity
        ) {
            return null;
        }

        const { canManage } = meta;
        const manageTooltip = canManage
            ? undefined
            : COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP;

        const canDelete = row.original.canDelete;
        const isArchived = row.original.archivedAt != null;
        const deleteTooltip = canManage
            ? COA_TEMPLATE_DELETE_FORBIDDEN_TOOLTIP
            : COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP;
        const archiveTooltip = canManage
            ? COA_TEMPLATE_ARCHIVE_FORBIDDEN_TOOLTIP
            : COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP;

        return (
            <ViewTableCell captureClicks>
                <HStack gap={8} align="center" justify="end">
                    {!isArchived && (
                        <Tooltip content={manageTooltip}>
                            <Button
                                variant="primary"
                                size="sm"
                                className={viewTableStyles.showOnHover}
                                disabled={!canManage}
                                onClick={() =>
                                    meta.callbacks?.onApplyTemplate(templateId)
                                }
                            >
                                Apply
                            </Button>
                        </Tooltip>
                    )}
                    <Menu.More className={viewTableStyles.showOnHover}>
                        <Menu.Item
                            disabled={!canManage}
                            disabledReason={manageTooltip}
                            onClick={() =>
                                meta.callbacks?.onEditTemplate(templateId)
                            }
                        >
                            Edit details
                        </Menu.Item>
                        <Menu.Item
                            disabled={!canManage}
                            disabledReason={manageTooltip}
                            onClick={() =>
                                meta.callbacks?.onEditTemplateAccounts(
                                    templateId,
                                )
                            }
                        >
                            Edit template
                        </Menu.Item>
                        <Menu.Item
                            disabled={!canManage}
                            disabledReason={manageTooltip}
                            onClick={() =>
                                meta.callbacks?.onDuplicateTemplate(templateId)
                            }
                        >
                            Duplicate
                        </Menu.Item>
                        <ExportMenuItem
                            isDownloading={isDownloading}
                            onClick={() => void downloadCsv(templateId)}
                        >
                            Export as CSV
                        </ExportMenuItem>
                        <ChartOfAccountsTemplateActivityAction
                            canManage={canManage}
                            onSelect={() =>
                                meta.callbacks?.onViewActivity(templateId)
                            }
                        />
                        <ChartOfAccountsTemplateArchiveAction
                            isArchived={isArchived}
                            canArchive={canDelete}
                            disabledReason={archiveTooltip}
                            onSelect={() =>
                                meta.callbacks?.onArchiveToggleTemplate(
                                    templateId,
                                )
                            }
                        />
                        <Menu.Item
                            destructive
                            disabled={!canDelete}
                            disabledReason={deleteTooltip}
                            onClick={() =>
                                meta.callbacks?.onDeleteTemplate(templateId)
                            }
                        >
                            Delete
                        </Menu.Item>
                    </Menu.More>
                </HStack>
            </ViewTableCell>
        );
    },
);
